/* eslint-disable @typescript-eslint/no-explicit-any */
import { htmlSpecialChars } from 'src/util/url';
import { toArray } from 'src/util/converter';
import { getBoundingRectangle, calculateUnrotatedWidthAndHeight } from 'src/util/math';
import idGeneratorAtor from 'src/util/idGeneratorAtor';
import { COMPOSITE, DOCUMENT, INSTRUCTION, MASK } from 'src/pages/editor/components/layer/layerTypes';
import {
    ENGRAVING_CHANNEL,
    LETTERPRESS_MODE_SOFTLIGHT,
    LETTERPRESS_MODES,
    LetterPressMode,
    NORMAL,
} from 'src/config/constants';
import { CYLINDER_WARP, DOCUMENT_ID, SMOOTH_WARP, TEXTURE_WARP } from 'src/models/documentWarps';
import { deflateRaw } from 'pako';
import { Document, ENGRAVING_COLOR } from 'src/pages/editor/components/documents/slice';
import { SurfaceType } from 'src/types/surface';
import {
    AutoMaskInstructionType,
    DocumentXmlObject,
    EngravingImageType,
    XMLCompositeType,
    XMLImageType,
    XMLTree_CompositeComposite,
    XMLTree_DIP,
} from 'src/types/xml';
import { PointType } from 'src/types';
import { MapPoint, TransformTypeField } from 'src/types/transforms';
import { FinishData } from 'src/services/materialClient';
import { EffectType } from 'src/config/effects';

/**
 * Type definitions for composite structures
 */
type CompositeWithDocuments = {
    composite: XMLTree_CompositeComposite;
    mainDocument?: any;
    effectWrapper?: { type: EffectType; document: any; transformId?: string };
    mode: string;
};

/**
 * Effect transform data extracted from Transforms section
 */
type EffectTransformData = {
    letterpress?: {
        id: string;
        depth: number;
    };
    glow?: {
        id: string;
        r: number;
        g: number;
        b: number;
        radius: number;
        light: boolean;
    };
};

const idGenerator = idGeneratorAtor(DOCUMENT_ID);

const BACKGROUND_DOC_PLACEHOLDER = (color: string) => ({
    documentId: 'demoEllipseDocument',
    version: '2',
    deleted: false,
    document: {
        panels: [
            {
                id: 'surface1',
                name: 'Item',
                width: '1mm',
                height: '1mm',
                shapes: [
                    {
                        type: 'rectangle',
                        zIndex: 0,
                        position: {
                            x: '0mm',
                            y: '0mm',
                            width: '1mm',
                            height: '1mm',
                        },
                        id: 'shape1',
                        color: `rgb(${color})`,
                        itemType: 'shape',
                    },
                ],
                decorationTechnology: 'offsetOrDigital',
            },
        ],
    },
    fontRepositoryUrl:
        'https://fonts.documents.cimpress.io/v1/repositories/aff15d65-e10f-492d-b8ea-cfd454c93c3f/published',
});

function getSizeFromPoints(points: MapPoint[], type: TransformTypeField) {
    let { width, height } = calculateUnrotatedWidthAndHeight(points);
    const sourceBox = getBoundingRectangle(points.map((point) => ({ x: point.sourceX, y: point.sourceY })));
    if (sourceBox.width !== 1 || sourceBox.height !== 1) {
        width /= sourceBox.width;
        height /= sourceBox.height;
    }

    if (type === SMOOTH_WARP) {
        const length = Math.ceil(Math.max(width, height));
        return `${length},${length}`;
    } else {
        return `${Math.ceil(width)},${Math.ceil(height)}`;
    }
}

/* eslint-disable no-underscore-dangle */
export function buildDocumentObject(doc: Document) {
    const { page } = doc;

    let size;
    if (doc.width || doc.height) {
        size = `${doc.width},${doc.height}`;
    } else {
        const { points, type } = (doc.transforms || [])[0];
        if (points) {
            size = getSizeFromPoints(points, type);
        } else {
            size = '1,1';
        } // default size if no points or dimensions are available
    }

    const docData: DocumentXmlObject = {
        size,
        name: doc.id,
        type: DOCUMENT,
        mode: doc.mode,
        page,
        offset: 0,
    };

    if (
        doc.colorProfile !== undefined &&
        doc.colorProfile !== null &&
        doc.colorProfile !== '' &&
        doc.colorProfile !== 'RGB'
    ) {
        docData.colorProfile = doc.colorProfile;
    }
    return docData;
}

export function buildMaskObject(doc: Document, size: string) {
    const { id, maskName, maskUrl } = doc;
    if (!maskUrl) {
        return undefined;
    }

    return {
        size,
        src: htmlSpecialChars(maskUrl),
        name: maskName || `${id}-Mask`,
        type: MASK,
        mode: MASK,
        depth: 0,
    };
}

function buildInstructionsContent(docSize: PointType, surface: SurfaceType) {
    const { trimAreas, fullBleedAreas } = surface;

    const Contours = trimAreas.map((trimArea, i) => {
        const widthScaler = docSize.x / fullBleedAreas[i].width;
        const heightScaler = docSize.y / fullBleedAreas[i].height;
        const { pathPoints } = trimArea;

        // Always start in the middle of the document
        const StartPointX = trimArea.anchorX * widthScaler;
        const StartPointY = trimArea.anchorY * heightScaler;

        const calculateSegments = pathPoints.map((pathPoint) => {
            const isCubic = pathPoint.firstControlPointX !== undefined && pathPoint.secondControlPointX !== undefined;
            let extra = {};
            const type = isCubic ? 'CubicTo' : 'LineTo';
            if (isCubic) {
                extra = {
                    ControlPoint1: {
                        X: pathPoint.firstControlPointX * widthScaler,
                        Y: pathPoint.firstControlPointY * heightScaler,
                    },
                    ControlPoint2: {
                        X: pathPoint.secondControlPointX * widthScaler,
                        Y: pathPoint.secondControlPointY * heightScaler,
                    },
                };
            }
            return {
                Type: type,
                ...extra,
                EndPoint: {
                    X: pathPoint.endPointX * widthScaler,
                    Y: pathPoint.endPointY * heightScaler,
                },
            };
        });

        return {
            Closed: true,
            StartPoint: {
                X: StartPointX,
                Y: StartPointY,
            },
            ContourSegments: calculateSegments,
        };
    });
    return JSON.stringify([
        {
            Type: 'Path',
            Fill: {
                Color: {
                    R: 0,
                    G: 0,
                    B: 0,
                    A: 255,
                },
            },
            PathContours: {
                Contours,
            },
        },
    ]);
}

export function buildAutomask(doc: DocumentXmlObject, surface: SurfaceType | undefined): AutoMaskInstructionType {
    const sizeValues = doc.size.split(',');
    const docSize = {
        x: parseInt(sizeValues[0]),
        y: parseInt(sizeValues[1]),
    };
    const content = surface ? buildInstructionsContent(docSize, surface) : undefined;

    return {
        size: doc.size,
        name: `${doc.name}-Mask`,
        mode: MASK,
        depth: 0,
        type: INSTRUCTION,
        content,
    };
}

export function buildEngravingImage(size: string, color = ENGRAVING_COLOR): EngravingImageType {
    const [height, width] = size.split(',');
    const content = JSON.stringify(BACKGROUND_DOC_PLACEHOLDER(color));
    const deflatedContent = deflateRaw(content, { to: 'string' });
    const encodedContent = encodeURIComponent(btoa(deflatedContent));
    const instructionsUri = `https://instructions.documents.cimpress.io/v3/instructions:preview?documentUri=${encodedContent}`;

    return {
        size,
        src: htmlSpecialChars(
            `https://rendering.documents.cimpress.io/v1/cse/preview?width=${width}&height=${height}&` +
                `format=webp&instructions_uri=${encodeURIComponent(instructionsUri)}`,
        ),
        name: 'Silver-Engraving',
        mode: NORMAL,
        depth: 1,
    };
}

export function buildFinishComposite(props: {
    finish: FinishData;
    doc: DocumentXmlObject;
    depth: number;
    size?: string;
}): XMLCompositeType {
    const { finish, doc, depth, size } = props;

    const docXMLObj: DocumentXmlObject = {
        ...doc,
        name: `${finish.name} Channel Mask`,
        mode: MASK,
        channel: finish.scene.channel,
        depth: 0,
    };

    const imageXMLObj: XMLImageType = {
        size: size ?? doc.size,
        name: `Tiled Finish Base - ${finish.name}`,
        mode: NORMAL,
        depth: 1,
        src: finish.scene.baseTexture.url,
        tileScale: 0.0,
        type: 'Image',
    };

    return {
        size: size ?? doc.size,
        name: `Document Finish Composite - ${finish.name}`,
        depth,
        xform: `${doc.name} ${finish.name} Transform`,
        children: [imageXMLObj, docXMLObj],
        type: COMPOSITE,
        mode: NORMAL,
    };
}

const hasMask = (composite: XMLTree_CompositeComposite) => {
    let founded = false;
    Object.keys(composite).forEach((key) => {
        if (founded) {
            return;
        }

        const ele = composite[key];
        if (key === 'Image' && ele._mode === MASK) {
            founded = true;
        } else if (typeof ele === 'object') {
            founded = hasMask(ele);
        }
    });

    return founded;
};

const hasAutomask = (composite: XMLTree_CompositeComposite) => {
    let founded = false;
    Object.keys(composite).forEach((key) => {
        if (founded) {
            return;
        }

        const ele = composite[key];
        if (key === 'Instructions' && ele._mode === MASK) {
            founded = true;
        }
    });

    return founded;
};

const getFinishes = (
    composite: XMLTree_CompositeComposite,
    finishes: { finish: FinishData; xform: string }[] = [],
    finishChannels: string[],
    finishData: Record<string, FinishData>,
) => {
    if (Array.isArray(composite)) {
        composite.forEach((comp) => getFinishes(comp, finishes, finishChannels, finishData));
    } else if (composite.Document?._channel && finishChannels.includes(composite.Document._channel)) {
        finishes.push({
            finish: finishData[composite.Document._channel],
            xform: composite._xform ?? composite.Document._xform,
        });
    } else if (composite.Composite) {
        getFinishes(composite.Composite, finishes, finishChannels, finishData);
    }

    return;
};

const sortDocuments = (a: XMLTree_CompositeComposite, b: XMLTree_CompositeComposite): number =>
    a._name < b._name ? -1 : a._name > b._name ? 1 : 0;

/**
 * Analyzes a composite wrapper to extract document and effect information
 * Handles both new structure (nested composites with wrappers) and legacy structure (document arrays)
 */
function analyzeCompositeStructure(composite: XMLTree_CompositeComposite): CompositeWithDocuments {
    const result: CompositeWithDocuments = {
        composite,
        mode: composite._mode || NORMAL,
    };

    let documentNode = composite.Document;

    // Strategy 1: Check for effect wrappers in nested composites (new structure)
    if (composite.Composite) {
        const childComposites = toArray(composite.Composite);

        // Look for letterpress wrapper
        const letterPressWrapper = childComposites.find((c: any) => c._name?.includes('-Letterpress-wrapper'));
        if (letterPressWrapper?.Document) {
            result.effectWrapper = {
                type: EffectType.LETTERPRESS,
                document: letterPressWrapper.Document,
                transformId: letterPressWrapper.Document._xform,
            };
            // Main document is a sibling, keep existing documentNode
        }

        // Look for glow wrapper
        const glowWrapper = childComposites.find((c: any) => c._name?.includes('-Glow-wrapper'));
        if (glowWrapper?.Document) {
            result.effectWrapper = {
                type: EffectType.GLOW,
                document: glowWrapper.Document,
                transformId: glowWrapper.Document._xform,
            };
            // For glow, the document is inside the wrapper
            documentNode = glowWrapper.Document;
            documentNode._xform = composite.Composite._xform ?? documentNode._xform;
        }

        // Fallback: deeply nested composite without wrappers
        if (!documentNode && !result.effectWrapper) {
            documentNode =
                composite.Composite.Document ||
                (composite.Composite.Composite && composite.Composite.Composite.Document);
        }
    }

    result.mainDocument = documentNode;
    return result;
}

/**
 * Extracts letterpress effect data from effect wrapper and transform data
 * @param effectWrapper - The wrapper containing the letterpress document
 * @param effectTransforms - Map of transform ID to effect transform data
 */
function extractLetterPressEffect(
    effectWrapper: { document: any; transformId?: string },
    effectTransforms: Map<string, EffectTransformData>,
): {
    type: EffectType.LETTERPRESS;
    mode: LetterPressMode;
    depth: number;
} {
    const letterPressDoc = effectWrapper.document;
    const mode = letterPressDoc._mode as LetterPressMode;

    // Get depth from transform data (source of truth), fallback to document element
    let depth = parseInt(letterPressDoc._depth);
    if (effectWrapper.transformId && effectTransforms.has(effectWrapper.transformId)) {
        const transformData = effectTransforms.get(effectWrapper.transformId);
        if (transformData?.letterpress) {
            depth = transformData.letterpress.depth;
        }
    }

    return {
        type: EffectType.LETTERPRESS,
        mode: (LETTERPRESS_MODES.includes(mode) ? mode : LETTERPRESS_MODE_SOFTLIGHT) as LetterPressMode,
        depth,
    };
}

/**
 * Extracts glow effect data (placeholder values, actual values set from GlowFilter in fromXml)
 */
function extractGlowEffect(
    effectWrapper: { document: any; transformId?: string },
    effectTransforms: Map<string, EffectTransformData>,
): {
    type: EffectType.GLOW;
    r: number;
    g: number;
    b: number;
    radius: number;
    light: boolean;
} {
    if (effectWrapper.transformId && effectTransforms.has(effectWrapper.transformId)) {
        const transformData = effectTransforms.get(effectWrapper.transformId);
        if (transformData?.glow) {
            return {
                type: EffectType.GLOW,
                r: transformData.glow.r,
                g: transformData.glow.g,
                b: transformData.glow.b,
                radius: transformData.glow.radius,
                light: transformData.glow.light,
            };
        }
    }

    return {
        type: EffectType.GLOW,
        r: 0,
        g: 0,
        b: 0,
        radius: 10,
        light: false,
    };
}

/**
 * Extracts engraving effect data
 */
function extractEngravingEffect(): {
    type: EffectType.ENGRAVING;
    color: string;
} {
    return {
        type: EffectType.ENGRAVING,
        color: '#808080', // Default, will be overridden from metadata if present
    };
}

/**
 * Extracts dimensions from document node for specific warp types
 */
function extractDocumentDimensions(
    documentNode: any,
    warpType: TransformTypeField,
): { width?: number; height?: number } {
    if (warpType === CYLINDER_WARP || warpType === TEXTURE_WARP) {
        const size = documentNode._size.split(',');
        return {
            width: parseFloat(size[0]),
            height: parseFloat(size[1]),
        };
    }
    return {};
}

/**
 * Builds a Document object from analyzed composite structure
 */
function buildDocumentFromComposite(
    structure: CompositeWithDocuments,
    transformIdMap: Record<string, TransformTypeField>,
    finishList: FinishData[],
    effectTransforms: Map<string, EffectTransformData>,
): Document | undefined {
    const { mainDocument, effectWrapper, mode } = structure;

    if (!mainDocument) {
        return undefined;
    }

    // Create base document
    const doc: Document = {
        mode,
        warpType: transformIdMap[mainDocument._xform],
        page: parseInt(mainDocument._page),
        colorProfile: mainDocument._colorProfile,
        id: mainDocument._name || idGenerator(),
        transforms: [],
    };

    // Extract dimensions for cylinder/texture warps
    Object.assign(doc, extractDocumentDimensions(mainDocument, doc.warpType as TransformTypeField));

    // Apply effects based on wrapper type or channel
    if (effectWrapper) {
        if (effectWrapper.type === EffectType.LETTERPRESS) {
            doc.effect = extractLetterPressEffect(effectWrapper, effectTransforms);
        } else if (effectWrapper.type === EffectType.GLOW) {
            doc.effect = extractGlowEffect(effectWrapper, effectTransforms);
            // Glow requires explicit dimensions
            const size = mainDocument._size.split(',');
            doc.width = parseFloat(size[0]);
            doc.height = parseFloat(size[1]);
        }
    }

    // Check for engraving channel
    if (mainDocument._channel === ENGRAVING_CHANNEL) {
        doc.effect = extractEngravingEffect();
    }

    // Extract mask information
    if (hasMask(structure.composite) && structure.composite.Image) {
        doc.maskUrl = structure.composite.Image._src;
        doc.maskName = structure.composite.Image._name;
    }

    // Check for automask
    if (hasAutomask(structure.composite)) {
        doc.automask = true;
    }

    // Extract finishes
    const finishData = finishList.reduce(
        (acc, finish) => {
            acc[finish.scene.channel] = finish;
            return acc;
        },
        {} as Record<string, FinishData>,
    );
    const finishes: { finish: FinishData; xform: string }[] = [];
    getFinishes(structure.composite, finishes, Object.keys(finishData || {}), finishData);

    if (finishes.length > 0) {
        doc.finishes = finishes.map(({ finish }) => finish);
    }

    return doc;
}

/**
 * Parses effect transforms (LetterpressFilter, GlowFilter) from Transforms section
 * These contain the actual effect parameters that are the source of truth
 */
function parseEffectTransforms(dip: XMLTree_DIP): Map<string, EffectTransformData> {
    const effectTransforms = new Map<string, EffectTransformData>();

    // Parse LetterpressFilter transforms
    const letterPressFilters = toArray(dip.Transforms.LetterpressFilter || []);
    letterPressFilters.forEach((filter: any) => {
        effectTransforms.set(filter._id, {
            letterpress: {
                id: filter._id,
                depth: parseInt(filter._depth),
            },
        });
    });

    // Parse GlowFilter transforms
    const glowFilters = toArray(dip.Transforms.GlowFilter || []);
    glowFilters.forEach((filter: any) => {
        effectTransforms.set(filter._id, {
            glow: {
                id: filter._id,
                r: parseInt(filter._r),
                g: parseInt(filter._g),
                b: parseInt(filter._b),
                radius: parseInt(filter._radius),
                light: filter._light === 'true',
            },
        });
    });

    return effectTransforms;
}

/**
 * REFACTORED: Parse XML DIP structure into Document objects
 * This is a cleaner, more maintainable version of xmlToDocumentObjects
 *
 * @param dip - The parsed XML DIP structure
 * @param finishList - List of available finishes
 * @returns Object containing documents array and finish transform names
 */
export function xmlToDocumentObjects(dip: XMLTree_DIP, finishList: FinishData[]) {
    // Step 1: Parse effect transforms first (source of truth for effect parameters)
    const effectTransforms = parseEffectTransforms(dip);

    // Step 2: Build transform ID to warp type mapping
    const transformIdMap: Record<string, TransformTypeField> = {};
    const addWarp = (name: string, warpType: TransformTypeField) =>
        toArray(dip.Transforms[name]).forEach((transform: Record<string, any>) => {
            transformIdMap[transform._id] = warpType;
        });

    addWarp('RectangleWarp', 'RectangleWarp');
    addWarp('PerspectiveWarp', 'PerspectiveWarp');
    addWarp('TubeWarp', 'CylinderWarp');
    addWarp('PlacementWarp', 'CylinderWarp'); // TODO: Check why they are using cylinder for both tube and placement
    addWarp('SmoothWarp', 'SmoothWarp');
    addWarp('TextureWarp', 'TextureWarp');

    // Step 3: Extract all composites from the root
    const composites: XMLTree_CompositeComposite[] = toArray(dip.Composite.Composite);

    // Step 4: Process each composite to extract document information
    const documents = composites
        .slice()
        .sort(sortDocuments) // Order matters for depth layering
        .map((composite) => {
            // Analyze composite structure to extract document and effect information
            const structure = analyzeCompositeStructure(composite);

            // Build document from analyzed structure with effect transform data
            return buildDocumentFromComposite(structure, transformIdMap, finishList, effectTransforms);
        })
        .filter((doc): doc is Document => doc !== undefined);

    // Step 4: Collect finish transform names (used to filter transforms later)
    const finishTransformNames: string[] = [];
    documents.forEach((doc) => {
        if (doc.finishes && doc.finishes.length > 0) {
            // Find the finishes in the original composites to get their xform IDs
            const composite = composites.find((c) => c.Document?._name === doc.id || c._name?.includes(doc.id));
            if (composite) {
                const finishes: { finish: FinishData; xform: string }[] = [];
                const finishData = finishList.reduce(
                    (acc, finish) => {
                        acc[finish.scene.channel] = finish;
                        return acc;
                    },
                    {} as Record<string, FinishData>,
                );
                getFinishes(composite, finishes, Object.keys(finishData || {}), finishData);
                finishTransformNames.push(...finishes.map((finish) => finish.xform));
            }
        }
    });

    return { documents, finishTransformNames };
}
